/**
 * RecentCommitsWidget — dashboard card showing the latest git commits
 * linked to tasks in the tenant. Populated mostly by Claude (via MCP),
 * but works for any client that calls POST /mcp link_commit.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GitCommit, Bot } from 'lucide-react';
import { apiClient, getErrorMessage } from '../packages/api-client';
import { StateView } from '../packages/ui';
import { TaskDrawer } from './TaskDrawer';

interface RecentCommit {
    id: string;
    sha: string;
    short_sha: string;
    branch?: string;
    repo_url?: string;
    pr_url?: string;
    message?: string;
    author_agent?: string;
    task_id: string;
    task_title: string;
    task_status: string;
    project_id: string;
    project_name: string;
    created_at: string;
    evidence_status: 'unverified';
}

function relDate(iso: string): string {
    const d = new Date(iso);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 14) return `${days}d ago`;
    return d.toLocaleDateString();
}

function commitURL(c: RecentCommit): string | null {
    if (!c.repo_url) return null;
    const base = c.repo_url.replace(/\.git$/, '').replace(/\/$/, '');
    if (base.includes('github.com') || base.includes('gitlab.com') || base.includes('bitbucket.org')) {
        return `${base}/commit/${c.sha}`;
    }
    return null;
}

export function RecentCommitsWidget() {
    const [commits, setCommits] = useState<RecentCommit[]>([]);
    const [loading, setLoading] = useState(true);
    // A failed load is shown with Retry — never as "No commits linked yet".
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [drawerTaskId, setDrawerTaskId] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        apiClient.get<{ data: RecentCommit[] }>('/activity/commits', { params: { limit: 8 } })
            .then(r => { if (!cancelled) setCommits(r.data.data ?? []); })
            .catch(err => { if (!cancelled) setError(getErrorMessage(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [attempt]);

    const title = <h2 id="recent-commits-title" style={{ fontSize: 'var(--font-size-base)', fontWeight: 650, margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}><GitCommit size={16} aria-hidden="true" /> Recent commits</h2>;

    if (loading || error) {
        return (
            <section className="card" aria-labelledby="recent-commits-title">
                <div style={{ marginBottom: 'var(--space-3)' }}>{title}</div>
                {error
                    ? <StateView state="error" title="Couldn't load recent commits" message={error} onRetry={() => setAttempt(n => n + 1)} />
                    : <StateView state="loading" label="Loading recent commits" />}
            </section>
        );
    }

    if (commits.length === 0) return null;

    return (
        <section className="card" aria-labelledby="recent-commits-title">
            <div style={{ marginBottom: 'var(--space-3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {title}
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {commits.length} commit{commits.length !== 1 ? 's' : ''}
                </span>
            </div>

            {commits.length === 0 ? (
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', padding: 'var(--space-3)', textAlign: 'center', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)' }}>
                    No commits linked yet. Generate an API key in Profile → API Keys and connect an MCP-capable agent.
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {commits.map(c => {
                        const url = commitURL(c);
                        return (
                            <div
                                key={c.id}
                                onClick={() => setDrawerTaskId(c.task_id)}
                                style={{
                                    display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start',
                                    padding: 'var(--space-2) var(--space-3)', background: 'var(--color-surface-2)',
                                    borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)',
                                    cursor: 'pointer',
                                }}
                            >
                                <div
                                    style={{ fontFamily: 'monospace', fontSize: 12, padding: '2px 6px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 4, flexShrink: 0 }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    {url ? (
                                        <a href={url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                                            {c.short_sha}
                                        </a>
                                    ) : (
                                        c.short_sha
                                    )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {c.message || c.task_title}
                                    </div>
                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ color: 'var(--color-primary)' }}>{c.task_title}</span>
                                        <span>·</span>
                                        <Link
                                            to={`/projects/${c.project_id}`}
                                            onClick={e => e.stopPropagation()}
                                            style={{ color: 'inherit', textDecoration: 'none' }}
                                        >
                                            {c.project_name}
                                        </Link>
                                        {c.branch && <><span>·</span><span>{c.branch}</span></>}
                                        <span>·</span>
                                        <span>{relDate(c.created_at)}</span>
                                        {c.author_agent && <><span>·</span><span title="Authored by" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Bot size={12} aria-hidden="true" /> {c.author_agent}</span></>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {drawerTaskId && (
                <TaskDrawer taskId={drawerTaskId} onClose={() => setDrawerTaskId(null)} />
            )}
        </section>
    );
}
